import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {BuilderV2Service} from "../../builder-v2.service";
import {PageMetaI, SecondaryPageReqI} from "../../builder-v2.interface";
import {CommonBuilderDataService} from "../../services/common-builder-data.service";
import {BuilderSectionsInteractionService} from "../../builder-sections-interaction.service";
import {SinglePageService} from "../services/single-page.service";
import {ActivatedRoute, Router} from "@angular/router";
import {first} from "rxjs/operators";
import {BuilderHistoryService} from "../../builder-history.service";
import {NotifierComponent} from "../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../shared/components/notifier/notification";
import {NotificationType} from "../../../shared/components/notifier/notification-type";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {Website} from "../../../websites/website/website";

enum GenerationSteps {
  START,
  OUTLINE,
  GENERATION
}

@Component({
    selector: 'db-single-page-builder',
    templateUrl: './single-page-builder.component.html',
    styleUrl: './single-page-builder.component.scss',
    standalone: false
})
export class SinglePageBuilderComponent implements OnDestroy, OnInit {
  openOutline;
  generationSteps = GenerationSteps;
  activeStep = GenerationSteps.START;
  headerSection = {
    "id": new Date().getTime(),
    "index": 0,
    "section_description": "Header section contains the blog's logo on the left and a navigation menu on the right, allowing easy access to different categories like Recipes, Tips, and About.",
    "section_title": "Header",
    "section_type": "header"
  };
  footerSection = {
    "id": new Date().getTime(),
    "index": 3,
    "section_description": "Footer section contains links to social media profiles on the left and copyright information on the right, ensuring easy navigation and legal clarity.",
    "section_title": "Footer",
    "section_type": "footer"
  };
  formTouched = false;
  needReset = true;
  isMobile = window.innerWidth <= 991;

  @Input('uniqueId') set uniqueId(id) {
    if (id) {
      this._getSiteData();
      this._sendGaEvent();
    }
  }
  @Input() website: Website;

  constructor(private _builderService: BuilderV2Service,
              public _builderInteractionsService: BuilderSectionsInteractionService,
              private _singlePageService: SinglePageService,
              private _router: Router,
              private _builderHistoryService: BuilderHistoryService,
              private _googleAnalyticsService: GoogleAnalyticsService,
              private _route: ActivatedRoute,
              public _builderCommonData: CommonBuilderDataService) {
  }

  ngOnInit() {
    const value = this._builderHistoryService.getPageFromStorage();
    this._builderCommonData.secondaryPagesLimit = 9;
    this._builderCommonData.secondaryPagesActiveSectionsLimit = 7;
    this._builderCommonData.domainId = this.getDomainIdFromUrl();
    if (value) {
      this._singlePageService.pageForm = value.page_form;
      this._singlePageService.page.next(value.single_page);
      this._singlePageService.siteData = value.site_data;
      this.activeStep = this.generationSteps.OUTLINE;
    }
  }

  private getDomainIdFromUrl(): number {
    const urlSegments = this._route.snapshot.url.map(segment => segment.path).join('/');
    const match = urlSegments.match(/(\d+)/); // Regular expression to find numbers
    return match ? +match[0] : this._builderCommonData.domainId;
  }

  private _getSiteData() {
    const {domainId, uniqueId} = this._builderCommonData;
    this._builderService.getSiteData(domainId, uniqueId)
      .pipe(first(resp => !!resp?.data))
      .subscribe(res => {
        const {primary_color, secondary_color, font, style, woocommerce, ...rest} = res.data;
        this._builderCommonData.websiteType = woocommerce && woocommerce?.woocommerce_active ? 'ecommerce' : 'basic';

        this._singlePageService.siteData = {
          ...rest,
          colors: {primary_color, secondary_color},
          theme: style,
          woocommerce,
          website_design_type_level: 1,
          fonts: {
            "primary_font": font
          },
        };
      });
  }

  onNext(page: { title: string, description: string }) {
    this.openOutline = true;
    this.activeStep = GenerationSteps.OUTLINE;
    if (this.formTouched) {
      this._getSinglePage(page);
    }
  }

  generate() {
    this.activeStep = GenerationSteps.GENERATION;
    const page_outline = this._builderInteractionsService.getSinglePageGenerationData();
    const data = {
      ...this._singlePageService.siteData,
      page_title: this._singlePageService.pageForm.title,
      page_type: this._singlePageService.page.value?.page_type,
      page_description: this._singlePageService.pageForm.description,
      slug: this._singlePageService.page.value.slug,
      website_type: this._builderCommonData.websiteType,
      post_status: 'draft',
      page_outline
    };
    const {domainId, uniqueId} = this._builderCommonData;
    this._builderService.generateSinglePage(data, domainId, uniqueId)
      .pipe(first(resp => !!resp)).subscribe((resp) => {
      if (!resp.success) {
        NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
      }
    }, error => {
      NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
    });
  }

  private _getSinglePage(page: { title: string, description: string }) {
    let additional = this._getAdditionalDataForPage();
    const data: SecondaryPageReqI = {
      ...additional,
      page_title: page.title,
      page_description: page.description,
      id: new Date().getTime(),
      standalone: 'yes',
      added_by_user: true,
      website_type: this._builderCommonData.websiteType
    };

    if (data.website_type === 'ecommerce') {
      data.added_by_user = true;
    }
    const {domainId, uniqueId} = this._builderCommonData;
    this._builderInteractionsService.loading.next(true);
    this._builderInteractionsService.showSectionsLoading.next(true);
    this._builderService.getSecondaryPage(data, domainId, uniqueId)
      .pipe(first(resp => !!resp))
      .subscribe(value => {
          if (value?.data) {
            this._setSinglePage(value.data);
          }
          if (!value?.success) {
            NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
          }
        },
        error => {
          NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
        }
      );
  }

  private _getAdditionalDataForPage() {
    if (this._singlePageService.siteData) {
      const {business_name, business_description, business_type} = this._singlePageService.siteData;
      return {business_name, business_description, business_type};
    }

    return {};
  }


  private _setSinglePage(data) {
    const {id, slug, page_description, page_title, page_type, index, page_outline} = data;
    page_outline.sections.unshift(this.headerSection);
    page_outline.sections.push(this.footerSection);
    const page: PageMetaI = {
      id,
      slug,
      description: page_description,
      title: page_title,
      page_type,
      index,
      sections: page_outline.sections
    };
    this._singlePageService.page.next(page);
    this._builderInteractionsService.loading.next(false);
  }

  onBackClick() {
    if (this.formTouched) {
      this._singlePageService.page.next(null);
      this._builderHistoryService.resetSubjects();
      this._builderInteractionsService.resetSubjects();
    }
    this.activeStep = GenerationSteps.START;
  }

  onIconClick() {
    this._router.navigate(['../']);
  }

  private _sendGaEvent(): void {
    const eventLabel = this._route.snapshot.queryParamMap.get('fromDashboard');
    const analyticsData = {
      eventCategory: "Dashboard Action",
      eventAction: 'Section-based Page Generation - Flow start',
      eventLabel: eventLabel ? 'Dashboard' : 'Website',
      uniqueId: this._builderCommonData.uniqueId
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCoreWithoutUserData(analyticsData);
  }

  ngOnDestroy() {
    this._singlePageService.pageForm = null;
    this._singlePageService.page.next(null);
    this._builderHistoryService.removePageFromStorage();
    this._builderHistoryService.resetSubjects();
    this._builderInteractionsService.resetSubjects();
    this._builderCommonData.secondaryPagesLimit = 5;
    this._builderCommonData.secondaryPagesActiveSectionsLimit = 3;
  }
}
